import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "./UrineAndUremiaSection.css";

const urinarySymptoms = [
  "nocturia", "pollakiuria", "incontinence", "twoStage", "dysuria", "hematuria",
];

const dipstickParameters = [
  { key: "hematuria" },
  { key: "ketones" },
  { key: "glucose" },
  { key: "proteinuria" },
  { key: "leukocyturia" },
];

const statusByResult = { "+": "low", "++": "moderate", "+++": "high" };

const uremicSigns = ["asthenia", "anorexia", "nausea", "vomiting", "pruritus", "dyspnea", "edema"];

export default function UrineAndUremiaSection({
  patientId,
  urinaryRecord,
  urinaryLoading = false,
  urinaryError,
}) {
  const { t } = useTranslation("neurologue");
  const [selectedSymptoms, setSelectedSymptoms] = useState([]);
  const [selectedSigns, setSelectedSigns] = useState([]);
  const [dipstickResults, setDipstickResults] = useState({});
  const [bowel, setBowel] = useState("");
  const [ph, setPh] = useState("");
  const [glycemia, setGlycemia] = useState("");

  useEffect(() => {
    const hydrateChoices = (values, choices) => {
      const stored = Array.isArray(values) ? values : [];
      return choices.filter((choice) =>
        stored.some((value) =>
          value === choice || ["fr", "en", "ar"].some((language) =>
            value === t(`urine.${choice}`, { lng: language })
          )
        )
      );
    };
    const record = urinaryRecord || {};
    setSelectedSymptoms(hydrateChoices(record.urinarySymptoms, urinarySymptoms));
    setSelectedSigns(hydrateChoices(record.uremicSigns, uremicSigns));
    setDipstickResults(record.bu || {});
    setBowel(record.bm || "");
    setPh(record.bu?.ph === null || record.bu?.ph === undefined ? "" : String(record.bu.ph));
    setGlycemia(record.glycemia === null || record.glycemia === undefined ? "" : String(record.glycemia));
  }, [patientId, urinaryRecord?.id, urinaryRecord, t]);

  const toggleItem = (item, setter) => {
    setter((current) => current.includes(item)
      ? current.filter((value) => value !== item)
      : [...current, item]);
  };

  const selectResult = (parameter, result) => {
    setDipstickResults((current) => ({ ...current, [parameter]: result }));
  };

  return (
    <section className="neurologue-section-card urine-uremia">
      <div className="urine-uremia__block">
        <h3>{t("urine.symptoms")}</h3>
        <div className="urine-uremia__chips">
          {urinarySymptoms.map((item) => (
            <button
              type="button"
              className={`urine-uremia__chip${selectedSymptoms.includes(item) ? " is-selected" : ""}`}
              aria-pressed={selectedSymptoms.includes(item)}
              data-value={item}
              key={item}
              onClick={() => toggleItem(item, setSelectedSymptoms)}
            >{t(`urine.${item}`)}</button>
          ))}
        </div>
      </div>

      <label className="urine-uremia__bowel">
        <span>{t("urine.bowel")}</span>
        <input type="text" placeholder={t("urine.bowelPlaceholder")} value={bowel} onChange={(event) => setBowel(event.target.value)} />
      </label>

      <div className="urine-uremia__block">
        <h3>{t("urine.dipstick")}</h3>
        <div className="urine-uremia__table-wrap">
          <table className="urine-uremia__table">
            <thead>
              <tr><th>{t("urine.parameter")}</th><th>{t("urine.result")}</th><th>{t("urine.status")}</th></tr>
            </thead>
            <tbody>
              {dipstickParameters.map(({ key }) => {
                const result = dipstickResults[key];
                const status = statusByResult[result];
                return (
                <tr key={key}>
                  <td>{t(`urine.${key}`)}</td>
                  <td>
                    <div className="urine-uremia__result-options" role="group" aria-label={t("urine.resultAria", { name: t(`urine.${key}`) })}>
                      {["neg", "+", "++", "+++"].map((option) => (
                        <button
                          type="button"
                          className={result === option ? `is-active is-active--${option === "neg" ? "negative" : statusByResult[option]}` : ""}
                          aria-pressed={result === option}
                          key={option}
                          onClick={() => selectResult(key, option)}
                        >{option === "neg" ? t("urine.negative") : option}</button>
                      ))}
                    </div>
                  </td>
                  <td>{status && <span className={`urine-uremia__status urine-uremia__status--${status}`}><span className="urine-uremia__status-dot" aria-hidden="true" />{t(`urine.${status}`)}</span>}</td>
                </tr>
                );
              })}
              <tr>
                <td>{t("urine.ph")}</td>
              <td><span className="urine-uremia__ph"><input type="number" min="0" max="14" step="0.1" aria-label={t("urine.phAria")} value={ph} onChange={(event) => setPh(event.target.value)} /><span>pH</span></span></td>
                <td><span className="urine-uremia__reference">{t("urine.normalRange")}</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="urine-uremia__block urine-uremia__signs">
        <h3>{t("urine.uremicSigns")}</h3>
        <div className="urine-uremia__chips">
          {uremicSigns.map((item) => (
            <button
              type="button"
              className={`urine-uremia__chip${selectedSigns.includes(item) ? " is-selected" : ""}`}
              aria-pressed={selectedSigns.includes(item)}
              data-value={item}
              key={item}
              onClick={() => toggleItem(item, setSelectedSigns)}
            >{t(`urine.${item}`)}</button>
          ))}
        </div>
        <label className="urine-uremia__glycemia">
          <span>{t("urine.glycemia")}</span>
          <span className="urine-uremia__unit-input"><input type="number" step="0.1" aria-label={t("urine.glycemiaAria")} value={glycemia} onChange={(event) => setGlycemia(event.target.value)} /><i>{t("urine.unitMmolLower")}</i></span>
        </label>
      </div>
      {urinaryLoading && <p role="status">{t("common.loading", { defaultValue: "Chargement des données urinaires…" })}</p>}
      {urinaryError && <p role="alert">{String(urinaryError)}</p>}
    </section>
  );
}
